import React, { useState, useCallback, useRef, useEffect } from 'react'
import { 
  Plus, Play, Save, Download, Upload, Trash2, Settings, 
  Zap, MessageSquare, Mail, Database, GitBranch, Filter,
  Clock, Webhook, Code, BarChart3, X, Check, Copy, Send,
  CreditCard, DollarSign, Phone, Globe, Users, FileText,
  ShoppingCart, Calendar, Bell, Lock, Eye, Share2, Rocket
} from 'lucide-react'
import toast from 'react-hot-toast'

const WorkflowBuilder = () => {
  const [nodes, setNodes] = useState([])
  const [connections, setConnections] = useState([])
  const [selectedNode, setSelectedNode] = useState(null)
  const [draggingNode, setDraggingNode] = useState(null)
  const [connectingFrom, setConnectingFrom] = useState(null)
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [showPublishModal, setShowPublishModal] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [workflowName, setWorkflowName] = useState('Untitled Workflow')
  const canvasRef = useRef(null)

  const nodeTypes = [
    {
      category: 'Triggers',
      items: [
        { id: 'webhook', name: 'Webhook', icon: Webhook, color: 'blue', description: 'Trigger on HTTP request' },
        { id: 'schedule', name: 'Schedule', icon: Clock, color: 'purple', description: 'Run on schedule (cron)' },
        { id: 'manual', name: 'Manual', icon: Play, color: 'green', description: 'Manual trigger' },
        { id: 'form', name: 'Form Submit', icon: FileText, color: 'indigo', description: 'Trigger on form submission' },
        { id: 'email', name: 'Email Received', icon: Mail, color: 'red', description: 'Trigger on email' }
      ]
    },
    {
      category: 'AI Operations',
      items: [
        { id: 'chatgpt', name: 'ChatGPT', icon: MessageSquare, color: 'indigo', description: 'OpenAI ChatGPT' },
        { id: 'claude', name: 'Claude AI', icon: MessageSquare, color: 'purple', description: 'Anthropic Claude' },
        { id: 'sentiment', name: 'Sentiment Analysis', icon: BarChart3, color: 'pink', description: 'Analyze sentiment' },
        { id: 'classify', name: 'Text Classification', icon: Filter, color: 'orange', description: 'Classify text' },
        { id: 'translate', name: 'Translate', icon: Globe, color: 'blue', description: 'Language translation' }
      ]
    },
    {
      category: 'Integrations',
      items: [
        { id: 'stripe', name: 'Stripe', icon: CreditCard, color: 'purple', description: 'Payment processing' },
        { id: 'paypal', name: 'PayPal', icon: DollarSign, color: 'blue', description: 'PayPal payments' },
        { id: 'sendgrid', name: 'SendGrid', icon: Mail, color: 'blue', description: 'Email service' },
        { id: 'twilio', name: 'Twilio', icon: Phone, color: 'red', description: 'SMS & Voice' },
        { id: 'slack', name: 'Slack', icon: MessageSquare, color: 'purple', description: 'Slack messaging' },
        { id: 'google-analytics', name: 'Google Analytics', icon: BarChart3, color: 'orange', description: 'Analytics tracking' },
        { id: 'hubspot', name: 'HubSpot', icon: Users, color: 'orange', description: 'CRM integration' },
        { id: 'salesforce', name: 'Salesforce', icon: Database, color: 'blue', description: 'CRM platform' },
        { id: 'shopify', name: 'Shopify', icon: ShoppingCart, color: 'green', description: 'E-commerce' },
        { id: 'calendly', name: 'Calendly', icon: Calendar, color: 'blue', description: 'Scheduling' }
      ]
    },
    {
      category: 'Actions',
      items: [
        { id: 'send-email', name: 'Send Email', icon: Mail, color: 'red', description: 'Send email message' },
        { id: 'http', name: 'HTTP Request', icon: Code, color: 'yellow', description: 'Make HTTP call' },
        { id: 'database', name: 'Database Query', icon: Database, color: 'teal', description: 'Query database' },
        { id: 'notification', name: 'Send Notification', icon: Bell, color: 'orange', description: 'Push notification' },
        { id: 'webhook-send', name: 'Send Webhook', icon: Send, color: 'green', description: 'Send webhook' }
      ]
    },
    {
      category: 'Logic',
      items: [
        { id: 'condition', name: 'If/Else', icon: GitBranch, color: 'gray', description: 'Conditional logic' },
        { id: 'switch', name: 'Switch', icon: GitBranch, color: 'slate', description: 'Multiple conditions' },
        { id: 'filter', name: 'Filter', icon: Filter, color: 'blue', description: 'Filter data' },
        { id: 'transform', name: 'Transform', icon: Code, color: 'purple', description: 'Transform data' }
      ]
    }
  ]

  const workflowTemplates = [
    {
      id: 'customer-support',
      name: 'Customer Support Bot',
      description: 'AI-powered customer support with email notifications',
      icon: MessageSquare,
      nodes: [
        { type: 'webhook', name: 'Customer Query', position: { x: 100, y: 100 } },
        { type: 'chatgpt', name: 'AI Response', position: { x: 350, y: 100 } },
        { type: 'send-email', name: 'Send Reply', position: { x: 600, y: 100 } }
      ]
    },
    {
      id: 'lead-qualification',
      name: 'Lead Qualification',
      description: 'Qualify leads and sync to CRM',
      icon: Users,
      nodes: [
        { type: 'form', name: 'Lead Form', position: { x: 100, y: 100 } },
        { type: 'classify', name: 'Qualify Lead', position: { x: 350, y: 100 } },
        { type: 'hubspot', name: 'Add to CRM', position: { x: 600, y: 100 } }
      ]
    },
    {
      id: 'payment-processing',
      name: 'Payment Processing',
      description: 'Process payments and send receipts',
      icon: CreditCard,
      nodes: [
        { type: 'webhook', name: 'Payment Request', position: { x: 100, y: 100 } },
        { type: 'stripe', name: 'Process Payment', position: { x: 350, y: 100 } },
        { type: 'sendgrid', name: 'Send Receipt', position: { x: 600, y: 100 } }
      ]
    }
  ]

  const handleDragStart = (nodeType) => {
    setDraggingNode(nodeType)
  }

  const handleCanvasDrop = (e) => {
    e.preventDefault()
    if (!draggingNode) return

    const rect = canvasRef.current.getBoundingClientRect()
    const x = (e.clientX - rect.left - canvasOffset.x) / zoom
    const y = (e.clientY - rect.top - canvasOffset.y) / zoom

    const newNode = {
      id: `node-${Date.now()}`,
      type: draggingNode.id,
      name: draggingNode.name,
      icon: draggingNode.icon,
      color: draggingNode.color,
      position: { x, y },
      config: getDefaultConfig(draggingNode.id)
    }

    setNodes([...nodes, newNode])
    setDraggingNode(null)
    toast.success(`Added ${draggingNode.name} node`)
  }

  const getDefaultConfig = (nodeType) => {
    const configs = {
      'chatgpt': { apiKey: '', model: 'gpt-4', prompt: '', temperature: 0.7 },
      'stripe': { apiKey: '', amount: 0, currency: 'usd', description: '' },
      'sendgrid': { apiKey: '', from: '', to: '', subject: '', body: '' },
      'twilio': { accountSid: '', authToken: '', from: '', to: '', message: '' },
      'webhook': { url: '', method: 'POST', headers: {} },
      'http': { url: '', method: 'GET', headers: {}, body: '' },
      'schedule': { cron: '0 0 * * *', timezone: 'UTC' },
      'condition': { field: '', operator: 'equals', value: '' },
      'database': { query: '', connection: '' }
    }
    return configs[nodeType] || {}
  }

  const handleNodeClick = (node) => {
    setSelectedNode(node)
  }

  const handleDeleteNode = (nodeId) => {
    setNodes(nodes.filter(n => n.id !== nodeId))
    setConnections(connections.filter(c => c.from !== nodeId && c.to !== nodeId))
    setSelectedNode(null)
    toast.success('Node deleted')
  }

  const handleStartConnection = (nodeId) => {
    setConnectingFrom(nodeId)
  }

  const handleEndConnection = (nodeId) => {
    if (connectingFrom && connectingFrom !== nodeId) {
      const newConnection = {
        id: `conn-${Date.now()}`,
        from: connectingFrom,
        to: nodeId
      }
      setConnections([...connections, newConnection])
      toast.success('Nodes connected')
    }
    setConnectingFrom(null)
  }

  const handleCanvasMouseDown = (e) => {
    if (e.target === canvasRef.current) {
      setIsPanning(true)
      setPanStart({ x: e.clientX - canvasOffset.x, y: e.clientY - canvasOffset.y })
    }
  }

  const handleCanvasMouseMove = (e) => {
    if (isPanning) {
      setCanvasOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      })
    }
  }

  const handleCanvasMouseUp = () => {
    setIsPanning(false)
  }

  const handleZoom = (delta) => {
    setZoom(prev => Math.max(0.5, Math.min(2, prev + delta)))
  }

  const handleRunWorkflow = () => {
    if (nodes.length === 0) {
      toast.error('Add nodes to run workflow')
      return
    }
    toast.success('🚀 Workflow execution started!')
    // Simulate execution
    setTimeout(() => {
      toast.success('✅ Workflow completed successfully!')
    }, 2000)
  }

  const handleSaveWorkflow = () => {
    const workflow = { 
      name: workflowName,
      nodes, 
      connections,
      createdAt: new Date().toISOString()
    }
    localStorage.setItem('workflow', JSON.stringify(workflow))
    toast.success('Workflow saved!')
  }

  const handleLoadWorkflow = () => {
    const saved = localStorage.getItem('workflow')
    if (saved) {
      const workflow = JSON.parse(saved)
      setNodes(workflow.nodes)
      setConnections(workflow.connections)
      setWorkflowName(workflow.name || 'Untitled Workflow')
      toast.success('Workflow loaded!')
    } else {
      toast.error('No saved workflow found')
    }
  }

  const handleExportWorkflow = () => {
    const workflow = {
      name: workflowName,
      nodes,
      connections,
      version: '1.0.0',
      exportedAt: new Date().toISOString()
    }
    const blob = new Blob([JSON.stringify(workflow, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${workflowName.replace(/\s+/g, '-').toLowerCase()}.json`
    a.click()
    toast.success('Workflow exported!')
  }

  const handlePublishWorkflow = () => {
    if (nodes.length === 0) {
      toast.error('Add nodes before publishing')
      return
    }
    setShowPublishModal(true)
  }

  const confirmPublish = () => {
    toast.success('🎉 Workflow published successfully!')
    setShowPublishModal(false)
  }

  const loadTemplate = (template) => {
    const templateNodes = template.nodes.map((node, index) => ({
      id: `node-${Date.now()}-${index}`,
      type: node.type,
      name: node.name,
      icon: nodeTypes.flatMap(cat => cat.items).find(n => n.id === node.type)?.icon || MessageSquare,
      color: nodeTypes.flatMap(cat => cat.items).find(n => n.id === node.type)?.color || 'blue',
      position: node.position,
      config: getDefaultConfig(node.type)
    }))
    
    setNodes(templateNodes)
    setWorkflowName(template.name)
    setShowTemplateModal(false)
    toast.success(`Template "${template.name}" loaded!`)
  }

  const getNodePosition = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId)
    return node ? node.position : { x: 0, y: 0 }
  }

  const updateNodeConfig = (key, value) => {
    if (!selectedNode) return
    const updatedNodes = nodes.map(node => {
      if (node.id === selectedNode.id) {
        return {
          ...node,
          config: { ...node.config, [key]: value }
        }
      }
      return node
    })
    setNodes(updatedNodes)
    setSelectedNode({ ...selectedNode, config: { ...selectedNode.config, [key]: value } })
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Top Toolbar */}
      <div className="fixed top-16 left-0 right-0 bg-white border-b border-gray-200 z-40 shadow-sm">
        <div className="max-w-full mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              className="text-xl font-bold text-gray-900 bg-transparent border-none outline-none focus:ring-2 focus:ring-blue-500 rounded px-2"
              placeholder="Workflow Name"
            />
            <span className="text-sm text-gray-500">• {nodes.length} nodes</span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTemplateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Templates
            </button>
            <button
              onClick={handleRunWorkflow}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Play className="w-4 h-4" />
              Test Run
            </button>
            <button
              onClick={handleSaveWorkflow}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
            <button
              onClick={handleLoadWorkflow}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Load
            </button>
            <button
              onClick={handleExportWorkflow}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={handlePublishWorkflow}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors shadow-md"
            >
              <Rocket className="w-4 h-4" />
              Publish
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-8rem)] mt-16">
        {/* Left Sidebar - Node Types */}
        <div className="w-72 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">Workflow Nodes</h2>
            
            {nodeTypes.map((category, idx) => (
              <div key={idx} className="mb-6">
                <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">{category.category}</h3>
                <div className="space-y-2">
                  {category.items.map((nodeType) => {
                    const Icon = nodeType.icon
                    return (
                      <div
                        key={nodeType.id}
                        draggable
                        onDragStart={() => handleDragStart(nodeType)}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-move hover:bg-gray-100 transition-colors border border-gray-200 hover:border-blue-300"
                      >
                        <div className={`w-8 h-8 bg-${nodeType.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-4 h-4 text-${nodeType.color}-600`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">{nodeType.name}</div>
                          <div className="text-xs text-gray-500 truncate">{nodeType.description}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative overflow-hidden bg-gray-50">
          <div
            ref={canvasRef}
            onDrop={handleCanvasDrop}
            onDragOver={(e) => e.preventDefault()}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            className="w-full h-full relative"
            style={{
              backgroundImage: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              cursor: isPanning ? 'grabbing' : 'grab'
            }}
          >
            {/* Zoom Controls */}
            <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-2 flex flex-col gap-2 z-30">
              <button
                onClick={() => handleZoom(0.1)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded"
              >
                <Plus className="w-4 h-4" />
              </button>
              <div className="text-xs text-center font-medium">{Math.round(zoom * 100)}%</div>
              <button
                onClick={() => handleZoom(-0.1)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded"
              >
                <span className="text-lg">−</span>
              </button>
            </div>

            {/* Connection Lines */}
            <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
              {connections.map((conn) => {
                const fromPos = getNodePosition(conn.from)
                const toPos = getNodePosition(conn.to)
                const x1 = (fromPos.x + 200) * zoom + canvasOffset.x
                const y1 = (fromPos.y + 40) * zoom + canvasOffset.y
                const x2 = toPos.x * zoom + canvasOffset.x
                const y2 = (toPos.y + 40) * zoom + canvasOffset.y
                
                return (
                  <g key={conn.id}>
                    <path
                      d={`M ${x1} ${y1} C ${x1 + 50} ${y1}, ${x2 - 50} ${y2}, ${x2} ${y2}`}
                      stroke="#3b82f6"
                      strokeWidth="2"
                      fill="none"
                    />
                    <circle cx={x2} cy={y2} r="4" fill="#3b82f6" />
                  </g>
                )
              })}
            </svg>

            {/* Nodes */}
            <div
              className="absolute"
              style={{
                transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
                zIndex: 10
              }}
            >
              {nodes.map((node) => {
                const Icon = node.icon
                return (
                  <div
                    key={node.id}
                    onClick={() => handleNodeClick(node)}
                    className={`absolute bg-white rounded-lg shadow-lg border-2 p-4 cursor-pointer transition-all ${
                      selectedNode?.id === node.id ? 'border-blue-500 shadow-xl' : 'border-gray-200'
                    }`}
                    style={{
                      left: node.position.x,
                      top: node.position.y,
                      width: '200px'
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 bg-${node.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 text-${node.color}-600`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{node.name}</div>
                        <div className="text-xs text-gray-500">{node.type}</div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteNode(node.id)
                        }}
                        className="w-6 h-6 flex items-center justify-center hover:bg-red-100 rounded text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Connection Points */}
                    <div className="flex justify-between">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEndConnection(node.id)
                        }}
                        className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                        title="Input"
                      >
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleStartConnection(node.id)
                        }}
                        className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors"
                        title="Output"
                      >
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Empty State */}
            {nodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">Start Building Your Workflow</h3>
                  <p className="text-gray-400">Drag nodes from the left sidebar or choose a template</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Node Configuration */}
        {selectedNode && (
          <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Node Settings</h2>
                <button
                  onClick={() => setSelectedNode(null)}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Node Name</label>
                  <input
                    type="text"
                    value={selectedNode.name}
                    onChange={(e) => {
                      setNodes(nodes.map(n => 
                        n.id === selectedNode.id ? { ...n, name: e.target.value } : n
                      ))
                      setSelectedNode({ ...selectedNode, name: e.target.value })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Node Type</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                    {selectedNode.type}
                  </div>
                </div>

                {/* Node-specific configuration */}
                {selectedNode.type === 'chatgpt' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                      <input
                        type="password"
                        value={selectedNode.config.apiKey || ''}
                        onChange={(e) => updateNodeConfig('apiKey', e.target.value)}
                        placeholder="sk-..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                      <select 
                        value={selectedNode.config.model || 'gpt-4'}
                        onChange={(e) => updateNodeConfig('model', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="gpt-4">GPT-4</option>
                        <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prompt</label>
                      <textarea
                        rows="4"
                        value={selectedNode.config.prompt || ''}
                        onChange={(e) => updateNodeConfig('prompt', e.target.value)}
                        placeholder="Enter your prompt..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      ></textarea>
                    </div>
                  </>
                )}

                {selectedNode.type === 'stripe' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                      <input
                        type="password"
                        value={selectedNode.config.apiKey || ''}
                        onChange={(e) => updateNodeConfig('apiKey', e.target.value)}
                        placeholder="sk_test_..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                      <input
                        type="number"
                        value={selectedNode.config.amount || 0}
                        onChange={(e) => updateNodeConfig('amount', parseFloat(e.target.value))}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                      <select 
                        value={selectedNode.config.currency || 'usd'}
                        onChange={(e) => updateNodeConfig('currency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="usd">USD</option>
                        <option value="eur">EUR</option>
                        <option value="gbp">GBP</option>
                      </select>
                    </div>
                  </>
                )}

                {selectedNode.type === 'sendgrid' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                      <input
                        type="password"
                        value={selectedNode.config.apiKey || ''}
                        onChange={(e) => updateNodeConfig('apiKey', e.target.value)}
                        placeholder="SG...."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">From Email</label>
                      <input
                        type="email"
                        value={selectedNode.config.from || ''}
                        onChange={(e) => updateNodeConfig('from', e.target.value)}
                        placeholder="noreply@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">To Email</label>
                      <input
                        type="email"
                        value={selectedNode.config.to || ''}
                        onChange={(e) => updateNodeConfig('to', e.target.value)}
                        placeholder="user@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                      <input
                        type="text"
                        value={selectedNode.config.subject || ''}
                        onChange={(e) => updateNodeConfig('subject', e.target.value)}
                        placeholder="Email subject"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </>
                )}

                {selectedNode.type === 'webhook' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={`https://api.helplineagent.com/webhook/${selectedNode.id}`}
                          readOnly
                          className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                        />
                        <button 
                          onClick={() => {
                            navigator.clipboard.writeText(`https://api.helplineagent.com/webhook/${selectedNode.id}`)
                            toast.success('URL copied!')
                          }}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Method</label>
                      <select 
                        value={selectedNode.config.method || 'POST'}
                        onChange={(e) => updateNodeConfig('method', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="POST">POST</option>
                        <option value="GET">GET</option>
                        <option value="PUT">PUT</option>
                        <option value="DELETE">DELETE</option>
                      </select>
                    </div>
                  </>
                )}

                <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                  <Check className="w-4 h-4" />
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Publish Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Publish Workflow</h3>
                  <p className="text-sm text-gray-600">Deploy your workflow to production</p>
                </div>
              </div>
              <button
                onClick={() => setShowPublishModal(false)}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Workflow Name</label>
                <input
                  type="text"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Environment</label>
                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="production">Production</option>
                  <option value="staging">Staging</option>
                  <option value="development">Development</option>
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Workflow Summary</h4>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex justify-between">
                    <span>Total Nodes:</span>
                    <span className="font-semibold">{nodes.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Connections:</span>
                    <span className="font-semibold">{connections.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <span className="font-semibold text-green-600">Ready to Deploy</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Lock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                <p className="text-sm text-yellow-800">
                  This workflow will be deployed with SSL encryption and monitored 24/7
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowPublishModal(false)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmPublish}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors font-medium flex items-center gap-2"
              >
                <Rocket className="w-4 h-4" />
                Publish Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Workflow Templates</h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {workflowTemplates.map((template) => {
                  const Icon = template.icon
                  return (
                    <div
                      key={template.id}
                      className="border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer"
                      onClick={() => loadTemplate(template)}
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-gray-900 mb-1">{template.name}</h4>
                          <p className="text-sm text-gray-600">{template.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{template.nodes.length} nodes</span>
                        <span>•</span>
                        <span>Ready to use</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WorkflowBuilder